// @ts-nocheck
import React, { ChangeEvent } from 'react';
import styled from '../../theme';
import { CalculatorRadioGroup, RadioInput, RadioLabel } from './styles';
import { DraperyModule } from '../../redux';
import TrashCan from '../../assets/icons/trash.svg';
import SearchSelect, { SearchSelectProps } from '../UI/search_select/search_select';
import Tooltip from '../UI/tooltip';
import InfoIcon from '../../assets/icons/info.svg';
import { PartsAndSuppliesPricingTable } from './parts-supplies-pricing-table';

const COLON_VALIDATION_MESSAGE = 'Cannot contain colon';

type PartsAndSuppliesProps = {
  handleInputChangeForNumber(e: ChangeEvent<HTMLInputElement>): void;
  draperyOrderHardware: DraperyModule.DraperyOrderHardware;
  handleRemovePurchaser(field: 'purchaser' | 'receiver', value: number): void;
  updateHardwareState(e: any, newValue?: boolean, id?: number): void;
  handleSearchSelectChange(
    id: number,
    name: string,
    pathId: string,
    pathName: string,
    itemId?: number,
    search?: string
  ): void;
  handleSearchSelectClear(pathId: string, pathName: string): void;
  onSupplierSearch: SearchSelectProps['onSearch'];
  onAddSupplierByName: SearchSelectProps['onAddByName'];
  openSupplierModalClick(pathId: string, pathName: string): void;
  disabled: boolean;
  handleDeleteTableRow(
    index: number,
    arrayName: string,
    targetKey?: 'draperyOrderHardware' | 'draperyOrderHeadrail'
  ): void;
  onHardwarePricingChange(
    index: number,
    field: 'item' | 'estimateDescription' | 'cost' | 'quantity' | 'markup' | 'customerPrice',
    value: string,
    isShipping?: boolean
  ): void;
  onToggleHardwareShipping(include: boolean): void;
};

export const defaultPartsAndSuppliesOrderHardware = (): DraperyModule.DraperyOrderHardware => {
  return {
    id: 0,
    purchaser: 1,
    receiver: 1,
    supplier: '',
    supplierId: null,
    notes: '',
    hardwareItem: '',
    rods: [],
    otherItems: [
      {
        newItem: true,
        item: '',
        quantity: '',
        description: '',
        finish: '',
      },
      {
        newItem: true,
        item: '',
        quantity: '',
        description: '',
        finish: '',
      },
    ],
  };
};

const HardwareContainer = styled.div`
  padding-top: 48px;
  display: flex;
  flex-direction: column;
  gap: 48px;
`;

const HardwareHeader = styled.div`
  display: flex;
  justiy-content: space-between;
  gap: 38px;
  flex-wrap: wrap;
`;

const HardwareHeaderColumn = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  flex: 1;
  min-width: 375px;
  flex-direction: column;
`;

const HardwareHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  & .label {
    min-width: 120px;

    > span {
      font-weight: 500;
    }
  }

  & .textarea_container {
    flex: 1;

    & .textarea {
      display: flex;
      width: 100%;
      height: 100%;
      outline: none;
      border: 1px solid rgba(35, 35, 35, 0.15);
      border-radius: 8px;
      padding: 10px 8px;
      resize: none;

      &:focus {
        border-color: ${({ theme }) => theme.colors.primary};
      }

      &.disabled {
        opacity: 0.45;
        background-color: white;
      }
    }
  }
`;
const HardwareTable = styled.table`
  width: 100%;
`;
const HardwareTableHead = styled.thead`
  border-bottom: 1px solid #e2e2e2;

  & tr {
    & th {
      text-align: left;
      padding: 0px 24px 24px 0px;
    }

    & > :nth-child(1) {
      width: 150px;
    }

    & > :nth-child(2) {
      text-align: center;
      width: 124px;
    }
  }
`;

const HardwareTableBody = styled.tbody`
  & tr {
    & td {
      vertical-align: top;
      text-align: left;
      padding: 12px 24px 12px 0px;

      & input {
        border: 1px solid rgba(35, 35, 35, 0.15);
        border-radius: 8px;
        height: 100%;
        width: 100%;
        outline: none;
        padding: 0px 10px;
        height: 43px;

        &:focus {
          border-color: ${({ theme }) => theme.colors.primary};
        }

        &:disabled {
          background: white;
          opacity: 0.45;
        }

        &[data-invalid='true'] {
          border-color: #e11900;
        }
      }
    }

    & > :nth-child(1) {
      width: 150px;
      font-size: 14px;
    }

    & > :nth-child(2) {
      width: 124px;
    }
  }

  & > :nth-child(1) {
    & td {
      padding: 24px 24px 12px 0px;
    }
  }

  & .add_rod_btn {
    height: 40px;
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 500;
    font-size: 16px;

    &.disabled {
      color: #bebebe;
    }
  }
`;

const ItemFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ItemFieldError = styled.span`
  color: #e11900;
  font-size: 12px;
  line-height: 1.2;
`;

const PartsAndSuppliesOtherOrder = (props: PartsAndSuppliesProps): JSX.Element => {
  const {
    handleInputChangeForNumber,
    draperyOrderHardware,
    handleRemovePurchaser,
    updateHardwareState,
    handleSearchSelectChange,
    handleSearchSelectClear,
    onSupplierSearch,
    onAddSupplierByName,
    openSupplierModalClick,
    disabled,
    handleDeleteTableRow,
    onHardwarePricingChange,
    onToggleHardwareShipping,
  } = props;

  const purchaserTooltipText = (
    <>
      Parts & Supplies will be <u>excluded</u> on the estimate export if{' '}
      <strong>None</strong> is selected for Purchaser.
    </>
  );
  const receiverTooltipText = (
    <>
      Parts & Supplies will only appear under Components Tracking section if{' '}
      <strong>Workroom</strong> is selected for Purchaser or Receiver.
    </>
  );

  const handleEnterPressed = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    const key = e.key;
    if (key === 'Enter' || key === 'Return') {
      e.preventDefault();

      const textarea = e.currentTarget;
      const value = textarea.value;
      const selectionStart = textarea.selectionStart;
      const selectionEnd = textarea.selectionEnd;

      const part1 = value.slice(0, selectionStart);
      const part2 = value.slice(selectionEnd);

      const newValue = part1 + '\n' + part2;

      textarea.value = newValue;
      textarea.selectionStart = textarea.selectionEnd = selectionStart + 1;
    }
  };

  const pricingSource = draperyOrderHardware;

  const customerPriceRaw = pricingSource?.hardwareCustomerFacingPrice;
  const shippingCustomerPriceRaw = (pricingSource as any).hardwareSih?.customerPrice;

  const mainPricingRow = {
    id: 'hardware-main',
    item: pricingSource?.hardwareItem || '',
    estimateDescription: pricingSource?.hardwareDescription || '',
    quantity:
      pricingSource?.hardwareQuantity !== undefined && pricingSource?.hardwareQuantity !== null
        ? String(pricingSource.hardwareQuantity)
        : '1',
    cost:
      pricingSource?.hardwareUnitPrice !== undefined && pricingSource?.hardwareUnitPrice !== null
        ? String(pricingSource.hardwareUnitPrice)
        : '',
    markup:
      pricingSource?.hardwareMarkup !== undefined && pricingSource?.hardwareMarkup !== null
        ? String(pricingSource.hardwareMarkup)
        : '',
    customerPrice:
      customerPriceRaw === null
        ? null
        : customerPriceRaw !== undefined
          ? String(customerPriceRaw)
          : '',
  };

  const shippingPricingRow = {
    id: 'hardware-shipping',
    item: (pricingSource as any).hardwareSih?.item || '',
    estimateDescription:
      (pricingSource as any).hardwareSih?.estimateDescription ||
      'Shipping & Handling for Components',
    quantity:
      (pricingSource as any).hardwareSih?.quantity !== undefined &&
        (pricingSource as any).hardwareSih?.quantity !== null
        ? String((pricingSource as any).hardwareSih?.quantity)
        : '1',
    cost:
      (pricingSource as any).hardwareSih?.cost !== undefined &&
        (pricingSource as any).hardwareSih?.cost !== null
        ? String((pricingSource as any).hardwareSih?.cost)
        : '',
    markup:
      (pricingSource as any).hardwareSih?.markup !== undefined &&
        (pricingSource as any).hardwareSih?.markup !== null
        ? String((pricingSource as any).hardwareSih?.markup)
        : '',
    customerPrice:
      shippingCustomerPriceRaw === null
        ? null
        : shippingCustomerPriceRaw !== undefined
          ? String(shippingCustomerPriceRaw)
          : '',
    active: !!(pricingSource as any).hardwareSih?.active,
  };

  return (
    <HardwareContainer>
      <HardwareHeader>
        <HardwareHeaderColumn>
          <HardwareHeaderRow>
            <div
              className="label"
              style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: '140px' }}
            >
              <span style={{ fontSize: '16px' }}>Purchaser:</span>
              <Tooltip position="bottom" message={purchaserTooltipText}>
                <InfoIcon style={{ width: '16px', height: '16px' }} />
              </Tooltip>
            </div>

            <CalculatorRadioGroup style={{ paddingLeft: '0px' }}>
              <RadioLabel
                className={`hardware_purchaser ${draperyOrderHardware.purchaser === 1 ? 'active' : ''
                  }`}
              >
                <RadioInput
                  type="radio"
                  id="hardware-workroom"
                  name="draperyOrderHardware.purchaser"
                  value={1}
                  checked={draperyOrderHardware.purchaser === 1}
                  onClick={() => handleRemovePurchaser('purchaser', 1)}
                  onChange={handleInputChangeForNumber}
                  disabled={disabled}
                  uiDisabled={disabled}
                />
                Workroom
              </RadioLabel>

              <RadioLabel
                className={`hardware_purchaser ${draperyOrderHardware.purchaser === 2 ? 'active' : ''
                  }`}
              >
                <RadioInput
                  type="radio"
                  id="hardware-client"
                  name="draperyOrderHardware.purchaser"
                  value={2}
                  checked={draperyOrderHardware.purchaser === 2}
                  onClick={() => handleRemovePurchaser('purchaser', 2)}
                  onChange={handleInputChangeForNumber}
                  disabled={disabled}
                  uiDisabled={disabled}
                />
                Client (COM)
              </RadioLabel>

              <RadioLabel
                className={`hardware_purchaser ${draperyOrderHardware.purchaser === 3 ? 'active' : ''
                  }`}
              >
                <RadioInput
                  type="radio"
                  id="hardware-none"
                  name="draperyOrderHardware.purchaser"
                  value={3}
                  checked={draperyOrderHardware.purchaser === 3}
                  onClick={() => handleRemovePurchaser('purchaser', 3)}
                  onChange={handleInputChangeForNumber}
                  disabled={disabled}
                  uiDisabled={disabled}
                />
                None
              </RadioLabel>
            </CalculatorRadioGroup>
          </HardwareHeaderRow>

          <HardwareHeaderRow>
            <div
              className="label"
              style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: '140px' }}
            >
              <span style={{ fontSize: '16px' }}>Receiver:</span>
              <Tooltip position="bottom" message={receiverTooltipText}>
                <InfoIcon style={{ width: '16px', height: '16px' }} />
              </Tooltip>
            </div>

            <CalculatorRadioGroup style={{ paddingLeft: '0px' }}>
              <RadioLabel
                className={`hardware_purchaser ${draperyOrderHardware.receiver === 1 ? 'active' : ''
                  }`}
              >
                <RadioInput
                  type="radio"
                  id="hardware-receiver-workroom"
                  name="draperyOrderHardware.receiver"
                  value={1}
                  checked={draperyOrderHardware.receiver === 1}
                  onClick={() => handleRemovePurchaser('receiver', 1)}
                  onChange={handleInputChangeForNumber}
                  disabled={disabled}
                  uiDisabled={disabled}
                />
                Workroom
              </RadioLabel>

              <RadioLabel
                className={`hardware_purchaser ${draperyOrderHardware.receiver === 2 ? 'active' : ''
                  }`}
              >
                <RadioInput
                  type="radio"
                  id="hardware-receiver-client"
                  name="draperyOrderHardware.receiver"
                  value={2}
                  checked={draperyOrderHardware.receiver === 2}
                  onClick={() => handleRemovePurchaser('receiver', 2)}
                  onChange={handleInputChangeForNumber}
                  disabled={disabled}
                  uiDisabled={disabled}
                />
                Client (COM)
              </RadioLabel>

              <RadioLabel
                className={`hardware_purchaser ${draperyOrderHardware.receiver === 3 ? 'active' : ''
                  }`}
              >
                <RadioInput
                  type="radio"
                  id="hardware-receiver-none"
                  name="draperyOrderHardware.receiver"
                  value={3}
                  checked={draperyOrderHardware.receiver === 3}
                  onClick={() => handleRemovePurchaser('receiver', 3)}
                  onChange={handleInputChangeForNumber}
                  disabled={disabled}
                  uiDisabled={disabled}
                />
                None
              </RadioLabel>
            </CalculatorRadioGroup>
          </HardwareHeaderRow>

          <HardwareHeaderRow>
            <div className="label">
              <span>Supplier</span>
            </div>

            <div className="textarea_container">
              <SearchSelect
                name="draperyOrderHardware.supplierId"
                searchType="calculator"
                searchPage="calculator"
                searchItem="supplier"
                placeholder="Please select"
                keepKeyword
                onSearch={onSupplierSearch}
                onAddByName={onAddSupplierByName}
                openModalClick={() =>
                  openSupplierModalClick(
                    'draperyOrderHardware.supplierId',
                    'draperyOrderHardware.supplier'
                  )
                }
                onItemClick={(id, name) =>
                  handleSearchSelectChange(
                    id,
                    name,
                    'draperyOrderHardware.supplierId',
                    'draperyOrderHardware.supplier'
                  )
                }
                onKeywordCleared={() =>
                  handleSearchSelectClear(
                    'draperyOrderHardware.supplierId',
                    'draperyOrderHardware.supplier'
                  )
                }
                initialValue={draperyOrderHardware.supplier || ''}
                disabled={disabled}
                addButtonText="Add New Supplier"
              />
            </div>
          </HardwareHeaderRow>
        </HardwareHeaderColumn>

        <HardwareHeaderColumn>
          <HardwareHeaderRow>
            <div className="label">
              <span>Parts & Supplies notes</span>
            </div>
          </HardwareHeaderRow>

          <HardwareHeaderRow>
            <div className="textarea_container">
              <textarea
                className={`textarea ${disabled ? 'disabled' : ''}`}
                name="draperyOrderHardware.notes"
                value={draperyOrderHardware.notes}
                onChange={(e) => updateHardwareState(e)}
                onKeyDown={handleEnterPressed}
                rows={4}
                disabled={disabled}
              />
            </div>
          </HardwareHeaderRow>
        </HardwareHeaderColumn>
      </HardwareHeader>

      <PartsAndSuppliesPricingTable
        rows={[mainPricingRow]}
        shippingRow={shippingPricingRow}
        includeShipping={!!(pricingSource as any).hardwareSih?.active}
        disabled={disabled}
        showItemColumn
        onChange={onHardwarePricingChange}
        onToggleShipping={onToggleHardwareShipping}
        draperyOrderHardware={draperyOrderHardware}
      />

      <HardwareTable>
        <HardwareTableHead>
          <tr>
            <th>Item</th>
            <th>Qty</th>
            <th>Description</th>
            <th>Finish</th>
            <th></th>
          </tr>
        </HardwareTableHead>

        <HardwareTableBody>
          {draperyOrderHardware.otherItems &&
            draperyOrderHardware.otherItems.length > 0 &&
            draperyOrderHardware.otherItems.map((other, index) => {
              const canDeleteRow =
                !disabled && draperyOrderHardware.otherItems.length > 1;
              const itemHasColon =
                other.newItem && typeof other.item === 'string' && other.item.includes(':');
              return (
                <tr key={index}>
                  {other.newItem ? (
                    <td>
                      <ItemFieldContainer>
                        <input
                          name="draperyOrderHardware.otherItems.item"
                          value={other.item}
                          onChange={(e) => updateHardwareState(e, false, index)}
                          disabled={disabled}
                          type="text"
                          aria-invalid={itemHasColon}
                          data-invalid={itemHasColon ? 'true' : 'false'}
                        />
                        {itemHasColon && <ItemFieldError>{COLON_VALIDATION_MESSAGE}</ItemFieldError>}
                      </ItemFieldContainer>
                    </td>
                  ) : (
                    <td>{other.item}</td>
                  )}
                  <td>
                    <input
                      name="draperyOrderHardware.otherItems.quantity"
                      value={other.quantity}
                      onChange={(e) => updateHardwareState(e, false, index)}
                      disabled={disabled}
                      type="text"
                    />
                  </td>
                  <td>
                    <input
                      name="draperyOrderHardware.otherItems.description"
                      value={other.description}
                      onChange={(e) => updateHardwareState(e, false, index)}
                      disabled={disabled}
                      type="text"
                    />
                  </td>
                  <td style={{ paddingRight: '0' }}>
                    <input
                      name="draperyOrderHardware.otherItems.finish"
                      value={other.finish}
                      onChange={(e) => updateHardwareState(e, false, index)}
                      disabled={disabled}
                      type="text"
                    />
                  </td>
                  <td
                    style={{
                      padding: '0',
                      textAlign: 'center',
                      width: '50px',
                    }}
                  >
                    <button
                      style={{
                        opacity: canDeleteRow ? '1' : '.4',
                      }}
                      disabled={!canDeleteRow}
                      type="button"
                      onClick={() => handleDeleteTableRow(index, 'otherItems')}
                    >
                      <TrashCan
                        style={{
                          width: '25px',
                          height: '25px',
                        }}
                      />
                    </button>
                  </td>
                </tr>
              );
            })}

          <tr>
            <td style={{ textAlign: 'end' }} colSpan={4}>
              <button
                className={`add_rod_btn ${disabled ? 'disabled' : ''}`}
                type="button"
                name="draperyOrderHardware.otherItems"
                onClick={(e) => updateHardwareState(e, true)}
                disabled={disabled}
              >
                + Add other
              </button>
            </td>
          </tr>
        </HardwareTableBody>
      </HardwareTable>
    </HardwareContainer>
  );
};

export default PartsAndSuppliesOtherOrder;
